import React, { useContext, useEffect, useReducer, useRef, useState } from 'react';
import { useEffectOnActive } from 'keepalive-for-react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import {
    screenCheckNeededCount, describeReviewAction, describeReviewIssue, fetchSessionReportFresh, flagReviewLevel,
    isCheckNeededRow, isSameCoachSnapshot, pickCoachFromReport, REVIEW_LEVEL_ORDER, rowReviewReasons,
    SESSION_REPORT_SHEETS, SessionReport, SessionReportCoach, SessionReportResult, SessionReportRow, SessionReportSheetKey, sessionReportKeys,
    sessionReportQuery,
} from '../../features/sessionReport';
import { hasReportReviewWork, screenReportText, ReportInfoTip, ReportMonthContext, REVIEW_TONES } from './sessionReportPresentation';
import { findRowSupport, isOpenSupport } from '../../features/sessionReport/support';
import { SupportBadge, SupportContext, rowSupportPresentation } from './SessionReportSupport';
import { hasResolutionRefresh, resolutionDisplayKey } from '../../features/sessionReport/resolution';

export type ReviewDisplayRow = SessionReportRow & { coachName: string; sheetKey: SessionReportSheetKey };

/** 접어도 조회·동기화 시도 기록을 보존한다. 자동 대기열은 목록을 열 때만 채운다. */
export const SessionReportReviewList = ({ centerId, report, selectedReport, selectedCoachId = '', open, resolvedRequest = 0, paused = false, onRecalculate, onSyncSummary, onDetailCoachChange, onToggle, onMemberCountsChange, onSummaryCountsChange, summaryNotice, renderDetail }: {
    centerId: string;
    report: SessionReport;
    selectedReport?: SessionReport;
    selectedCoachId?: string;
    open: boolean;
    resolvedRequest?: number;
    summaryNotice?: string;
    onToggle: () => void;
    onMemberCountsChange: (counts: Record<string, number>) => void;
    onSummaryCountsChange?: (counts: { count: number; resolved: number; fix: number; ask: number; submitted?: number; ready: boolean; reasons: Record<string, number> }) => void;
    paused?: boolean;
    onRecalculate?: () => void;
    onSyncSummary?: (coachId: string, detailUpdatedAt: number) => Promise<unknown>;
    onDetailCoachChange?: (coachId: string | null) => void;
    renderDetail: (row: ReviewDisplayRow, onClose: () => void) => React.ReactNode;
}) => {
    const queryClient = useQueryClient();
    const supportList = useContext(SupportContext);
    const { month } = useContext(ReportMonthContext);
    const [, update] = useReducer((n: number) => n + 1, 0);
    const active = useRef(true);
    const shown = useRef(new Map<string, { coach: SessionReportCoach; rows: ReviewDisplayRow[] }>());
    const queued = useRef(new Set<string>());
    const refreshAfterFetch = useRef(new Set<string>());
    const failed = useRef(new Set<string>());
    const syncAttempted = useRef(new Set<string>());
    const [syncing, setSyncing] = useState(false);
    const [stage, setStage] = useState<string | null>(null);
    const resolvedGroup = useRef<HTMLDetailsElement>(null);
    const handledResolvedRequest = useRef(0);
    const [resolvedOpen, setResolvedOpen] = useState(false);
    const countsCallback = useRef(onMemberCountsChange);
    countsCallback.current = onMemberCountsChange;
    const summaryCountsCallback = useRef(onSummaryCountsChange);
    summaryCountsCallback.current = onSummaryCountsChange;
    const [detailKey, setDetailKey] = useState<{ coachId: string; rowKey: string } | null>(null);
    useEffect(() => {
        onDetailCoachChange?.(detailKey?.coachId ?? null);
    }, [detailKey?.coachId, onDetailCoachChange]);
    useEffect(() => () => onDetailCoachChange?.(null), [onDetailCoachChange]);
    useEffect(() => {
        if (selectedCoachId) setDetailKey((current) => current?.coachId === selectedCoachId ? current : null);
    }, [selectedCoachId]);
    const coaches = report.coaches.filter((coach) => {
        if (screenCheckNeededCount(coach.summary.reference) > 0 || (coach.summary.reference.resolvedCheckNeededRowCount ?? 0) > 0
            || shown.current.has(coach.coachId)) return true;
        const cached = queryClient.getQueryData<SessionReportResult>(sessionReportKeys.report(centerId, report.yearMonth, report.ghostDays, coach.coachId));
        const candidate = cached?.status === 'ok' ? cached.report : selectedReport;
        const detail = candidate?.yearMonth === report.yearMonth && candidate.ghostDays === report.ghostDays
            ? pickCoachFromReport(candidate, coach.coachId) : undefined;
        return !!detail && isSameCoachSnapshot(coach, detail)
            && SESSION_REPORT_SHEETS.every(({ key }) => Array.isArray(detail.sheets[key]?.rows));
    });
    // 요약에서 확인할 일이 사라져도 열린 상세의 강사 자료는 끝까지 대조한다.
    if (detailKey && !coaches.some((coach) => coach.coachId === detailKey.coachId)) {
        const previous = shown.current.get(detailKey.coachId)?.coach;
        if (previous) coaches.push(previous);
    }
    const definitions = coaches.map((coach) => sessionReportQuery({
        centerId, yearMonth: report.yearMonth, ghostDays: report.ghostDays, coachId: coach.coachId,
    }));
    useQueries({ queries: definitions.map((definition) => ({
        ...definition, enabled: false, notifyOnChangeProps: 'all' as const, retry: false, refetchOnWindowFocus: false,
    })) });
    useEffectOnActive(() => {
        active.current = true;
        update();
        return () => { active.current = false; setDetailKey(null); };
    }, []);
    useEffect(() => queryClient.getQueryCache().subscribe((event) => {
        if (event.query.queryKey[0] !== sessionReportKeys.all[0]) return;
        if (event.type === 'removed' && event.query.queryKey[1] === 'resolutionDisplay') { update(); return; }
        if (event.type !== 'updated') return;
        // 무효화는 전체 대기열을 다시 채우지 않는다. 열린 상세의 강사만 받는다.
        if (open && active.current && event.action.type === 'invalidate' && !event.query.isActive()
            && event.query.queryHash === queryClient.getQueryCache().find(sessionReportKeys.report(
                centerId, report.yearMonth, report.ghostDays, detailKey?.coachId ?? '',
            ))?.queryHash && detailKey && !hasResolutionRefresh(queryClient, {
                centerId, yearMonth: report.yearMonth, ghostDays: report.ghostDays, coachId: detailKey.coachId,
            })) {
            queued.current.add(detailKey.coachId);
            refreshAfterFetch.current.add(detailKey.coachId);
        }
        update();
    }), [queryClient, open, centerId, report.yearMonth, report.ghostDays, detailKey]);

    const entries = coaches.map((coach, index) => {
        const definition = definitions[index];
        const cachedQuery = queryClient.getQueryCache().find<SessionReportResult>(definition.queryKey);
        const state = cachedQuery?.state;
        const data = state?.data;
        const fetching = state?.fetchStatus === 'fetching' || state?.fetchStatus === 'paused';
        const isError = state?.status === 'error';
        const candidate = data?.status === 'ok' ? data.report : data ? undefined : selectedReport;
        const sameBasis = candidate?.yearMonth === report.yearMonth && candidate?.ghostDays === report.ghostDays;
        const detail = sameBasis ? pickCoachFromReport(candidate, coach.coachId) : undefined;
        const hasRows = !!detail && SESSION_REPORT_SHEETS.every(({ key }) => Array.isArray(detail.sheets[key]?.rows));
        if (hasRows && !isError && !fetching) failed.current.delete(coach.coachId);
        const matches = !!detail && isSameCoachSnapshot(coach, detail);
        const stale = !!data && !!cachedQuery?.isStaleByTime(definition.staleTime);
        const fresh = hasRows && !stale && !isError && !fetching;
        const allRows: ReviewDisplayRow[] = hasRows ? SESSION_REPORT_SHEETS.flatMap(({ key }) =>
            detail!.sheets[key].rows!.map((row) => ({ ...row, coachName: coach.coachName, sheetKey: key }))) : [];
        // 유효하게 받은 마지막 줄은 만료·무효화·재조회 실패 중에도 화면에 남긴다.
        if (hasRows && matches) shown.current.set(coach.coachId, { coach, rows: allRows });
        const detailUpdatedAt = state?.dataUpdatedAt ?? 0;
        const syncKey = `${coach.coachId}|${detailUpdatedAt}`;
        const mismatch = hasRows && !fetching && !isError && !matches;
        // 마지막 정상 목록은 상세 수명 때문에 남기되, 무효화·요약 변경 뒤 배지에 이전 건수를 쓰지 않는다.
        const canCountRows = hasRows && matches && !state?.isInvalidated && !isError && !fetching;
        return { coach, fresh, fetching, isError, stale, allRows, hasRows, mismatch, syncKey, detailUpdatedAt, canCountRows,
            failed: isError || failed.current.has(coach.coachId) };
    });

    const visibleEntries = selectedCoachId ? entries.filter(({ coach }) => coach.coachId === selectedCoachId) : entries;
    const wasOpen = useRef(false);
    const previousCoachId = useRef(selectedCoachId);
    useEffect(() => {
        // 진행 중 요청은 그대로 두고, 앞으로 받을 대기열만 현재 선택 범위로 좁힌다.
        if (selectedCoachId) {
            queued.current.forEach((id) => {
                if (id !== selectedCoachId) { queued.current.delete(id); refreshAfterFetch.current.delete(id); }
            });
        }
        if (open && (!wasOpen.current || previousCoachId.current !== selectedCoachId)) {
            visibleEntries.forEach((entry) => {
                if (!entry.fresh && !entry.failed && !entry.fetching) queued.current.add(entry.coach.coachId);
            });
        }
        wasOpen.current = open;
        previousCoachId.current = selectedCoachId;
        if (!open) setDetailKey(null);
    });
    useEffect(() => {
        if (!open || !active.current || paused || !centerId) return;
        for (const entry of visibleEntries) {
            const id = entry.coach.coachId;
            if (!queued.current.has(id)) continue;
            if (entry.fetching) {
                if (!refreshAfterFetch.current.has(id)) queued.current.delete(id);
                continue;
            }
            if (entry.fresh && !entry.mismatch && !refreshAfterFetch.current.has(id)) { queued.current.delete(id); continue; }
            const inFlight = queryClient.getQueryCache().findAll({
                queryKey: [...sessionReportKeys.all, 'report'],
                predicate: (query) => !!query.queryKey[5] && query.state.fetchStatus !== 'idle',
            }).length;
            if (inFlight >= 2) break;
            queued.current.delete(id);
            refreshAfterFetch.current.delete(id);
            failed.current.delete(id);
            void fetchSessionReportFresh(queryClient, {
                centerId, yearMonth: report.yearMonth, ghostDays: report.ghostDays, coachId: id,
            }).then((result) => {
                const coach = result.status === 'ok' && result.report.yearMonth === report.yearMonth && result.report.ghostDays === report.ghostDays
                    ? pickCoachFromReport(result.report, id) : undefined;
                if (!coach || !SESSION_REPORT_SHEETS.every(({ key }) => Array.isArray(coach.sheets[key]?.rows))) failed.current.add(id);
            }).catch(() => { failed.current.add(id); }).finally(update);
        }
    });
    useEffect(() => {
        if (!open || !active.current || paused || !centerId || syncing) return;
        const mismatches = visibleEntries.filter((entry) => entry.fresh && entry.mismatch && !syncAttempted.current.has(entry.syncKey)
            && !hasResolutionRefresh(queryClient, { centerId, yearMonth: report.yearMonth, ghostDays: report.ghostDays,
                coachId: entry.coach.coachId }, entry.detailUpdatedAt));
        if (!mismatches.length) return;
        // 같은 상세 응답당 한 번. 여러 강사가 달라도 요약 요청 하나로 함께 맞춘다.
        mismatches.forEach((entry) => syncAttempted.current.add(entry.syncKey));
        setSyncing(true);
        const request = onSyncSummary
            ? Promise.all(mismatches.map((entry) => onSyncSummary(entry.coach.coachId, entry.detailUpdatedAt)))
            : fetchSessionReportFresh(queryClient, { centerId, yearMonth: report.yearMonth, ghostDays: report.ghostDays });
        void request.catch(() => undefined).finally(() => setSyncing(false));
    });

    // 마지막 정상 자료·상세 수명은 보존하고 표시 범위를 고른 강사로 좁힌다.
    const displayRows = visibleEntries.flatMap(({ coach }) => (shown.current.get(coach.coachId)?.rows ?? []).map((row) => {
        const pending = queryClient.getQueryData<{ resolved: boolean; awaitingData?: SessionReportResult }>(resolutionDisplayKey({
            centerId, yearMonth: report.yearMonth, ghostDays: report.ghostDays, coachId: coach.coachId,
        }, row.rowKey));
        const data = queryClient.getQueryData(sessionReportKeys.report(centerId, report.yearMonth, report.ghostDays, coach.coachId));
        return pending && (!pending.awaitingData || pending.awaitingData === data) ? { ...row, resolved: pending.resolved } : row;
    }));
    const rows = displayRows.filter(isCheckNeededRow);
    const resolvedRows = displayRows.filter((row) => row.resolved === true);
    useEffect(() => {
        if (!resolvedRequest) { handledResolvedRequest.current = 0; return; }
        if (!open || handledResolvedRequest.current === resolvedRequest || !resolvedRows.length) return;
        handledResolvedRequest.current = resolvedRequest;
        setResolvedOpen(true);
        resolvedGroup.current?.scrollIntoView?.({ block: 'start' });
        resolvedGroup.current?.querySelector('summary')?.focus();
    }, [open, resolvedRequest, resolvedRows.length]);
    rows.sort((a, b) => REVIEW_LEVEL_ORDER.indexOf(flagReviewLevel(rowReviewReasons(a)[0]))
        - REVIEW_LEVEL_ORDER.indexOf(flagReviewLevel(rowReviewReasons(b)[0])));
    const detailEntry = entries.find(({ coach }) => coach.coachId === detailKey?.coachId);
    const detailRows = detailEntry?.hasRows && detailEntry.fresh ? detailEntry.allRows
        : shown.current.get(detailKey?.coachId ?? '')?.rows;
    const detailRow = detailRows?.find((row) => row.rowKey === detailKey?.rowKey);
    useEffect(() => {
        if (detailKey && detailEntry?.fresh && !detailRow) setDetailKey(null);
    }, [detailKey, detailEntry?.fresh, detailRow]);
    const pending = visibleEntries.filter((entry) => entry.fetching || queued.current.has(entry.coach.coachId)
        || entry.failed || entry.mismatch || entry.stale || !shown.current.has(entry.coach.coachId));
    const done = visibleEntries.filter((entry) => !entry.fetching && !queued.current.has(entry.coach.coachId)
        && (shown.current.has(entry.coach.coachId) || entry.fresh || entry.mismatch || entry.failed)).length;
    // 서버와 같은 회원×시트 행 단위예요. 같은 회원의 다른 행도 각각 세요.
    const rowCount = rows.length;
    const stages = REVIEW_LEVEL_ORDER.filter((level) => level !== 'info').map((level) => ({
        level, rows: rows.filter((row) => flagReviewLevel(rowReviewReasons(row)[0]) === level),
    })).filter((item) => item.rows.length > 0);
    const selectedStage = stages.find((item) => item.level === stage) ?? stages[0];
    const countsJson = JSON.stringify(Object.fromEntries(entries.filter((entry) => entry.canCountRows)
        .map(({ coach, allRows }) => [coach.coachId, allRows.filter(isCheckNeededRow).length])));
    useEffect(() => { countsCallback.current(JSON.parse(countsJson)); }, [countsJson]);
    const summaryCountsJson = JSON.stringify({ count: rowCount,
        resolved: new Set(resolvedRows.flatMap((row) => row.memberIds.length ? row.memberIds : [row.rowKey])).size,
        fix: rows.filter((row) => flagReviewLevel(rowReviewReasons(row)[0]) === 'fix').length,
        ask: rows.filter((row) => flagReviewLevel(rowReviewReasons(row)[0]) === 'ask').length,
        submitted: supportList?.status === 'ok' ? rows.filter((row) => {
            const request = findRowSupport(supportList.data.results, row);
            return flagReviewLevel(rowReviewReasons(row)[0]) === 'ask' && request && isOpenSupport(request);
        }).length : undefined,
        ready: visibleEntries.every((entry) => entry.canCountRows),
        reasons: rows.reduce<Record<string, number>>((counts, row) => {
            rowReviewReasons(row).forEach((reason) => { counts[reason] = (counts[reason] ?? 0) + 1; });
            return counts;
        }, {}),
    });
    useEffect(() => { summaryCountsCallback.current?.(JSON.parse(summaryCountsJson)); }, [summaryCountsJson]);
    return <div>
        <div className="mb-[10px] flex flex-wrap items-center justify-between gap-[16px]">
            <h2 className="text-[15px] font-semibold leading-[24px] text-[#202630]">
                {(() => {
                    const reference = selectedCoachId
                        ? report.coaches.find((coach) => coach.coachId === selectedCoachId)?.summary.reference
                        : report.centerTotals?.reference ?? report.coaches[0]?.summary.reference;
                    const summaryCount = screenCheckNeededCount(reference);
                    return summaryCount > 0 ? `${summaryCount}건 확인 필요`
                        : hasReportReviewWork(reference) || summaryNotice ? '확인할 기록이 있어요'
                        : selectedCoachId ? '이 강사는 손볼 회원이 없어요' : `${month}은 손볼 회원이 없어요`;
                })()}
            </h2>
            <div className="ml-auto flex items-center gap-[8px]">
            <ReportInfoTip label="확인할 일 건수 안내">제목은 서버 요약 기준이고, 단계 탭은 불러온 목록의 행 수예요. 같은 회원도 시트가 다르면 각각 한 건으로 세요. 목록을 다 받기 전에는 건수가 다를 수 있어요. 아래 이유별 건수는 이유별이에요. 한 기록에 이유가 여러 개면 건수가 달라요.</ReportInfoTip>
            <button type="button" aria-expanded={open} aria-controls="session-report-review" onClick={onToggle}
                className="min-h-[40px] shrink-0 text-[14px] text-[#4E5968]">{open ? '회원 목록 접기' : '회원 목록 펼치기'}</button>
            </div>
        </div>
        {rowCount === 0 && pending.length === 0 && summaryNotice && <p className="mb-[16px] rounded-[10px] bg-[#EFF5FF] px-[16px] py-[12px] text-[14px] leading-[22px] text-[#3D73DD]">
            {summaryNotice} · 회원 목록 밖에 확인할 기록이 있어요. 아래 이유별 안내를 확인해 주세요.
        </p>}
        {open && <div id="session-report-review">
        {stages.length > 0 && <div role="tablist" aria-label="손볼 회원 단계" className="flex gap-[16px] overflow-x-auto border-b border-solid border-[#E7EBF0]">
            {stages.map(({ level, rows: stageRows }, index) => <button type="button" role="tab" key={level}
                id={`review-tab-${level}`} aria-controls="session-report-review-panel" aria-selected={level === selectedStage?.level}
                tabIndex={level === selectedStage?.level ? 0 : -1}
                onKeyDown={(event) => {
                    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                    event.preventDefault();
                    const next = event.key === 'Home' ? 0 : event.key === 'End' ? stages.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + stages.length) % stages.length;
                    setStage(stages[next].level);
                    document.getElementById(`review-tab-${stages[next].level}`)?.focus();
                }}
                onClick={() => setStage(level)}
                className={`flex min-h-[50px] shrink-0 items-center gap-[8px] -mb-px border-b-2 border-solid py-[9px] text-[14px] font-semibold ${level === selectedStage?.level ? 'border-[#202630] text-[#202630]' : 'border-[#0000] text-[#4E5968]'}`}>
                {level === 'ask' ? '라포에 알릴 회원' : level === 'fix' ? '직접 고칠 회원' : '확인할 회원'}
                <span className="inline-flex h-[23px] min-w-[24px] items-center justify-center rounded-[6px] px-[6px] text-[14px] font-semibold" style={{ backgroundColor: level === selectedStage?.level ? REVIEW_TONES[level].bg : '#EEF1F5', color: level === selectedStage?.level ? REVIEW_TONES[level].text : '#4E5968' }}>{stageRows.length}건</span>
                {level === 'ask' && supportList?.status === 'ok' && <span className="text-[13px]">· 라포 확인 중 {stageRows.filter((row) => {
                    const request = findRowSupport(supportList.data.results, row);
                    return request && isOpenSupport(request);
                }).length}건</span>}
            </button>)}
        </div>}
        {done < visibleEntries.length && <p role="status" className="mb-[12px] text-[14px] leading-[22px] text-[#4E5968]">
            {`회원을 불러오고 있어요 (${done}/${visibleEntries.length})`}
        </p>}
        {pending.length > 0 && <ul aria-label="강사별 불러오기 상태" className="mb-[16px] rounded-[10px] bg-[#F4F5F7] px-[16px] py-[12px]">
            {pending.map((entry) => <li key={entry.coach.coachId} className="flex min-h-[48px] items-center justify-between gap-[12px] text-[14px] leading-[21px]">
                <span className="text-[#4E5968]">{entry.coach.coachName}</span>
                {entry.mismatch ? <span className="text-[#3D73DD]">{syncing ? '최신 요약을 불러오고 있어요' : '숫자가 방금 바뀌었어요 · '}
                    {!syncing && <button type="button" onClick={() => {
                        onRecalculate?.();
                        queued.current.add(entry.coach.coachId);
                        update();
                    }} className="min-h-[40px] underline">다시 계산</button>}</span>
                    : entry.failed && !entry.fetching && !queued.current.has(entry.coach.coachId) ? <span className="text-[#4E5968]">불러오지 못했어요 · <button type="button"
                        onClick={() => { queued.current.add(entry.coach.coachId); failed.current.delete(entry.coach.coachId); update(); }}
                        className="min-h-[40px] text-primary">다시 시도</button></span>
                    : <span className="text-[#4E5968]">{entry.fetching ? (shown.current.has(entry.coach.coachId) ? '다시 받는 중이에요' : '불러오는 중이에요')
                        : entry.stale && !queued.current.has(entry.coach.coachId) ? '이전 자료예요 · 목록을 다시 열면 새로 받아요' : '차례를 기다리고 있어요'}</span>}
            </li>)}
        </ul>}
        {rowCount > 0 && pending.length > 0 && <p className="my-[12px] text-[14px] leading-[22px] text-[#4E5968]">현재 확인된 회원이에요. {selectedCoachId ? '최신 자료를 받으면 건수가 바뀔 수 있어요.' : '나머지 강사 자료를 받으면 건수가 바뀔 수 있어요.'}</p>}
        <div role={selectedStage ? 'tabpanel' : undefined} id="session-report-review-panel" aria-labelledby={selectedStage ? `review-tab-${selectedStage.level}` : undefined}>
        <ul aria-label="손볼 회원 목록" className="divide-y divide-[#F2F4F6]">
            {(selectedStage?.rows ?? []).map((row, index) => {
                const reason = rowReviewReasons(row)[0];
                const request = supportList && supportList.status !== 'unavailable' ? findRowSupport(supportList.data.results, row) : undefined;
                const avatars = ['bg-[#EDF0F5] text-[#4D627C]', 'bg-[#EAF2F1] text-[#326C62]', 'bg-[#F1ECF8] text-[#6F558E]', 'bg-[#F9F0E6] text-[#896441]', 'bg-[#EEF1E8] text-[#687644]'];
                return <li key={`${row.coachId}:${row.rowKey}`} className="session-report-member-row flex flex-wrap items-center gap-[9px] border-b border-solid border-[#E9EDF2] py-[20px]">
                    <div className="flex w-full shrink-0 items-center gap-[9px]">
                        <span aria-hidden="true" className={`flex h-[29px] w-[29px] shrink-0 items-center justify-center rounded-[10px] text-[14px] font-semibold ${avatars[index % avatars.length]}`}>{Array.from(row.memberName.trim())[0]}</span>
                        <div className="min-w-0">
                            <span className="block break-words text-[15px] font-semibold leading-[24px] text-[#202630]">{row.memberName}</span>
                            <span className="block text-[14px] leading-[22px] text-[#4E5968]">{row.coachName} 강사</span>
                            <SupportBadge row={row} />
                        </div>
                    </div>
                    <div className="session-report-member-copy min-w-0 w-full">
                        <p className="text-[15px] font-semibold leading-[24px] text-[#202630]">{describeReviewIssue(reason)}</p>
                        <p className="mt-[3px] text-[14px] leading-[22px] text-[#4E5968]">{request || flagReviewLevel(reason) === 'ask' ? rowSupportPresentation(row, request).guidance : screenReportText(describeReviewAction(reason))}</p>
                    </div>
                    <button type="button" aria-label={`${row.memberName} 회원 수강권 확인`} onClick={() => {
                        setDetailKey({ coachId: row.coachId, rowKey: row.rowKey });
                        if (!entries.find(({ coach }) => coach.coachId === row.coachId)?.fresh) queued.current.add(row.coachId);
                    }} className="flex h-[40px] min-w-[111px] shrink-0 items-center justify-center gap-[8px] rounded-[10px] bg-[#EEF4FF] px-[12px] text-[14px] font-medium text-[#3D73DD] hover:bg-[#E0EBFF]">
                        수강권 확인
                        <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="m9 5 7 7-7 7" /></svg>
                    </button>
                </li>;
            })}
        </ul>
        </div>
        {rows.length === 0 && pending.length === 0 && <p className="py-[12px] text-[15px] text-[#4E5968]">{resolvedRows.length > 0 ? '지금 손볼 회원은 없어요. 확인을 끝낸 회원은 아래에서 다시 볼 수 있어요' : '불러온 자료에 손볼 회원 기록이 없어요. 이유별 안내도 확인해 주세요.'}</p>}
        {resolvedRows.length > 0 && <details ref={resolvedGroup} open={resolvedOpen} onToggle={(event) => setResolvedOpen(event.currentTarget.open)} className="mt-[20px] border-t border-solid border-[#E7EBF0] pt-[8px]">
            <summary className="cursor-pointer list-none py-[12px] text-[14px] font-medium text-[#4E5968]">
                처리 완료 {resolvedRows.length}건 <span aria-hidden="true">›</span>
            </summary>
            <ul aria-label="처리 완료 회원 목록" className="divide-y divide-[#F2F4F6]">
                {resolvedRows.map((row) => <li key={`${row.coachId}:${row.rowKey}`}>
                    <button type="button" aria-label={`${row.memberName} 회원 처리 완료 보기`}
                        onClick={() => {
                            setDetailKey({ coachId: row.coachId, rowKey: row.rowKey });
                            if (!entries.find(({ coach }) => coach.coachId === row.coachId)?.fresh) queued.current.add(row.coachId);
                        }} className="flex w-full items-center justify-between gap-[12px] py-[14px] text-left text-[14px]">
                        <span><span className="font-medium text-[#333D4B]">{row.memberName}</span>
                            <span className="ml-[8px] text-[#4E5968]">{row.coachName} 강사</span></span>
                        <SupportBadge row={row} /><span className="shrink-0 text-[#15803D]">처리 완료 ›</span>
                    </button>
                </li>)}
            </ul>
        </details>}
        </div>}
        {detailRow && renderDetail(detailRow, () => setDetailKey(null))}
    </div>;
};
